import useOverlayBadge from "@insite/client-framework/Common/Hooks/useOverlayBadge";
import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import sendProductRecommendationTracking from "@insite/client-framework/Store/Components/ProductRecommendations/Handlers/SendProductRecommendationTracking";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
// import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage"; // Valtech Custom Code
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
// import ProductPrice from "@insite/content-library/Components/ProductPrice"; // Valtech Custom Code
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import Badge from "@insite/mobius/Badge";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
// import { TypographyPresentationProps } from "@insite/mobius/Typography"; // Valtech Custom Code
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Valtech Custom Code */
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { useIsSignedIn, useMergeProps } from "@store/Hooks/Common";
import ProductImage, { ProductImageStyles } from "@store/Overrides/Components/ProductImage";
import { TYPOGRAPHY } from "@store/theme/design";
import { actionLinkStyles, contentSpacingStyles } from "@store/theme/mixins";
import { addToListLinkProps } from "@store/theme/props/addToListLink";
import { productCarouselProps } from "@store/theme/props/productCarousel";
/* Valtech Custom Code */

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productInfo: getProductInfoFromList(state, ownProps.carouselId, ownProps.product.id),
});

const mapDispatchToProps = {
    updateProductInfo,
    sendProductRecommendationTracking,
};

interface OwnProps {
    carouselId: string;
    product: ProductModel;
    recommendationId?: number;
    showImage: boolean;
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showPrice: boolean;
    showAddToCart: boolean;
    showAddToList: boolean;
    showImageBadges?: boolean;
    maximumNumberOfImageBadges?: number;
    showTextBadges?: boolean;
    maximumNumberOfTextBadges?: number;
    extendedStyles?: ProductCarouselProductStyles;
    // Valtech Custom Code
    overridedStyles?: ProductCarouselProductStyles;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ProductCarouselProductStyles {
    productImageStyles?: ProductImageStyles;
    productBrandWrapper?: InjectableCss;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    productPriceWrapper?: InjectableCss;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    productQuantityBreakPricingStyles?: ProductQuantityBreakPricingStyles;
    addToCartContainer?: GridContainerProps;
    qtyGridItem?: GridItemProps;
    productQuantityOrderedStyles?: TextFieldProps;
    addToCartButtonGridItem?: GridItemProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    badgeAreaWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
    // Valtech Custom Code
    partNumberText?: TypographyProps;
    contentWrapper?: InjectableCss;
    addToCartWrapper?: InjectableCss;
    productPrice?: ProductPriceStyles;
    productStartingAtText?: TypographyPresentationProps;
    productOptionsLinkWrapper?: InjectableCss;
    productOptionsLink?: LinkPresentationProps;
    productAddToListLinkWrapper?: InjectableCss;
}

export const productCarouselProductStyles: ProductCarouselProductStyles = {
    productImageStyles: {
        ...productCarouselProps.productImageStyles,
        wrapper: {
            ...productCarouselProps.productImageStyles?.wrapper,
            css: css`
                ${productCarouselProps.productImageStyles?.wrapper?.css}
                border: 1px solid ${getColor("common.border")};
            `,
        },
        image: {
            ...productCarouselProps.productImageStyles?.image,
            css: css`
                ${productCarouselProps.productImageStyles?.image?.css}
                padding: 10px;
            `,
        },
    },
    productBrandWrapper: productCarouselProps.productBrandWrapper,
    productBrandStyles: productCarouselProps.productBrandStyles,
    productDescriptionStyles: productCarouselProps.productDescriptionStyles,
    partNumberText: productCarouselProps.partNumberText,
    addToCartWrapper: productCarouselProps.addToCartWrapper,
    productPriceWrapper: productCarouselProps.productPriceWrapper,
    productPrice: productCarouselProps.productPrice,
    productStartingAtText: {
        size: TYPOGRAPHY.size.smaller,
        weight: TYPOGRAPHY.weight.bold,
    },
    productAddToListLinkStyles: addToListLinkProps,
    productOptionsLinkWrapper: {
        css: css`
            ${contentSpacingStyles("6px", null)}
        `,
    },
    productOptionsLink: {
        css: css`
            ${actionLinkStyles()}
        `,
    },
    productAddToListLinkWrapper: {
        css: css`
            ${contentSpacingStyles("6px", null)}
        `,
    },
    badgeAreaWrapper: {
        css: css`
            position: relative;
        `,
    },
    badgeTextWrapper: {
        css: css`
            position: absolute;
            width: 50%;
            white-space: nowrap;
            overflow: hidden;
        `,
    },
    badgeImageWrapper: {
        css: css`
            position: absolute;
            width: 25%;
            height: 25%;
        `,
    },
    badgeText: {
        weight: "bold",
        css: css`
            width: 100%;
            margin: auto;
            ${wrapInContainerStyles}
        `,
    },
};

const ProductCarouselProduct = ({
    carouselId,
    product,
    showImage,
    showBrand,
    showTitle,
    showPartNumbers,
    showPrice,
    showAddToCart,
    showAddToList,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
    updateProductInfo,
    extendedStyles,
    productInfo,
    recommendationId,
    sendProductRecommendationTracking,
    // Valtech Custom Code
    overridedStyles,
}: Props) => {
    /* Valtech Custom Code */
    const isSignedIn = useIsSignedIn();
    const styles = useMergeProps(Object.assign(productCarouselProductStyles, overridedStyles), extendedStyles);
    /* Valtech Custom Code */

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: carouselId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: carouselId, productId: product.id, unitOfMeasure });
    };

    if (!product || !productInfo) {
        return null;
    }

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    /* Valtech Custom Code */
    const hasVariants = productContext.product.isVariantParent;
    const productDetailPath = productContext.productInfo.productDetailPath;
    /* Valtech Custom Code */

    const trackProductRecommendation = (isNewPageWasOpen: boolean) => {
        if (recommendationId) {
            sendProductRecommendationTracking({ recommendationId, isNewPageWasOpen });
        }
    };

    const { showOverlayBadge, overlayBadge, overlayBadgeWrapperStyles } = useOverlayBadge({
        productContext,
        showTextBadge: showTextBadges,
        showImageBadge: showImageBadges,
        styles,
    });

    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });

    return (
        <>
            <ProductContext.Provider value={productContext}>
                {/* Valtech Custom Code */}
                {showImage && !showOverlayBadge && (
                    <ProductImage
                        product={productContext}
                        onClick={() => trackProductRecommendation(true)}
                        extendedStyles={styles.productImageStyles}
                        size="medium"
                    />
                )}
                {showImage && showOverlayBadge && overlayBadge && (
                    <StyledWrapper {...styles.badgeAreaWrapper} data-test-selector="productCarouselBadge">
                        <ProductImage extendedStyles={styles.productImageStyles} product={productContext} />
                        <StyledWrapper {...overlayBadgeWrapperStyles}>
                            <Badge
                                type={overlayBadge.badgeType}
                                value={
                                    overlayBadge.badgeType === "Image"
                                        ? overlayBadge.largeImageBadgePath
                                        : overlayBadge.displayText
                                }
                                badgeTextColor={overlayBadge.textColorHexCode}
                                badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                                badgeTextRounding={overlayBadge.badgeStyle === "Round" ? 20 : 0}
                                imageAltText={overlayBadge.imageAltText ? overlayBadge.imageAltText : overlayBadge.name}
                                textBadgeCss={styles.badgeText}
                                imageBadgeProps={styles.badgeImageProps}
                            />
                        </StyledWrapper>
                    </StyledWrapper>
                )}
                <StyledWrapper {...styles.contentWrapper}>
                    {showBrand && product.brand && (
                        <StyledWrapper {...styles.productBrandWrapper}>
                            <ProductBrand brand={product.brand} extendedStyles={styles.productBrandStyles} />
                        </StyledWrapper>
                    )}
                    {showTitle && (
                        <ProductDescription
                            product={productContext}
                            onClick={() => trackProductRecommendation(true)}
                            extendedStyles={styles.productDescriptionStyles}
                        />
                    )}
                    {showPartNumbers && (
                        <Typography {...styles.partNumberText} as="p" data-test-selector="ProductDetailsPartNumber">
                            <Typography>{translate("Part #")} - </Typography>
                            {product.productNumber}
                        </Typography>
                    )}
                    {showTextBadges && textBadges && (
                        <ProductTextBadges badgeList={textBadges} maxBadges={maximumNumberOfTextBadges ?? 4} />
                    )}
                    {showImageBadges && imageBadges && (
                        <ProductImageBadges badgeList={imageBadges} maxBadges={maximumNumberOfImageBadges ?? 4} />
                    )}
                    {showPrice && isSignedIn && (
                        <StyledWrapper {...styles.productPriceWrapper}>
                            {hasVariants ? (
                                <Typography {...styles.productStartingAtText}>
                                    {`${translate("Starting at")} ${
                                        productContext.productInfo.pricing?.actualPriceDisplay
                                    }`}
                                </Typography>
                            ) : (
                                <ProductPrice
                                    showLabel={false}
                                    product={productContext}
                                    extendedStyles={styles.productPrice}
                                />
                            )}
                            {/* Valtech Removed Code - product.unitOfMeasures */}
                            {/* Valtech Removed Code - ProductQuantityBreakPricing */}
                        </StyledWrapper>
                    )}
                    {hasVariants ? (
                        <StyledWrapper {...styles.productOptionsLinkWrapper}>
                            <Link
                                {...styles.productOptionsLink}
                                href={productDetailPath}
                                data-test-selector="productDescriptionLink"
                            >
                                {translate("Click to view options")}
                            </Link>
                        </StyledWrapper>
                    ) : null}
                    {showAddToCart && isSignedIn && (
                        <StyledWrapper {...styles.addToCartWrapper}>
                            <VisuallyHidden as="label" htmlFor={`${carouselId}-qty`} id={`${carouselId}-qty-label`}>
                                {translate("QTY")}
                            </VisuallyHidden>
                            <ProductQuantityOrdered
                                labelOverride=""
                                extendedStyles={styles.productQuantityOrderedStyles}
                                id={`${carouselId}-qty`}
                            />
                            <ProductAddToCartButton
                                onAddToCartClick={() => trackProductRecommendation(false)}
                                data-test-selector="addToCartBtn"
                                extendedStyles={styles.productAddToCartButtonStyles}
                                labelOverride={translate("Add")}
                            />
                        </StyledWrapper>
                    )}
                    {showAddToList && isSignedIn && (
                        <StyledWrapper {...styles.productAddToListLinkWrapper}>
                            <ProductAddToListLink
                                data-test-selector="addToListLink"
                                extendedStyles={styles.productAddToListLinkStyles}
                            />
                        </StyledWrapper>
                    )}
                    {/* Valtech Custom Code */}
                </StyledWrapper>
            </ProductContext.Provider>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductCarouselProduct);
